import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { DataTableNew } from "@/components/shared/DataTable";
import SearchInput from "@/components/shared/SearchInput";
import DateRangeFilter, { dateRangeParams } from "@/components/shared/DateRangeFilter";
import { Card } from "@/components/ui/Card";
import MultiSelectFilter from "@/components/shared/MultiSelectFilter";
import DetailModal from "../components/DetailModal";
import { AuditDashboardService } from "@/services/auditDashboardService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import Select from "@/components/ui/Select";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/Button";

const ACTOR_TYPES = [
  { value: "", label: "All actors" },
  { value: "user", label: "User" },
  { value: "superadmin", label: "SuperAdmin" },
  { value: "system", label: "System" },
];

const UserActionsTab = ({ tenantId }) => {
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [actorType, setActorType] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [filterOptions, setFilterOptions] = useState({ actions: [], entityTypes: [] });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    AuditDashboardService.fetchTenantActionsFilterOptions(tenantId)
      .then((res) => setFilterOptions({ actions: res?.actions ?? [], entityTypes: res?.entityTypes ?? [] }))
      .catch((err) => console.warn("Failed to load filter options", err));
  }, [tenantId]);

  const extraParams = useMemo(
    () => ({
      tenantId,
      ...(action && { action }),
      ...(entityType && { entityType }),
      ...(actorType && { actorType }),
      ...dateRangeParams(range),
    }),
    [tenantId, action, entityType, actorType, range]
  );

  // Service already returns { success, data: { pagination, items } } —
  // that's exactly the shape DataTableNew's api() prop expects, no wrapping.
  const api = useCallback((params) => AuditDashboardService.fetchTenantActions(params), []);

  const onRowClick = async (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await AuditDashboardService.fetchTenantActionDetail(row.id, tenantId);
      setDetail(res?.data ?? null);
    } catch (err) {
      toast.error(apiErrorMessageFormat(err));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

const COLUMNS = useMemo(
  () => [
    {
      key: "changedAt",
      sortKey: "changed_at",
      label: "Changed at",
      sortable: true,
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    { key: "action", sortKey: "action", label: "Action type", sortable: true },
    { key: "entityType", sortKey: "entity_type", label: "Entity type", sortable: true },
    {
      key: "entityId",
      label: "Entity ID",
      render: (v) => (v ? `${v.slice(0, 8)}…` : "—"),
    },
    {
      key: "actorType",
      label: "Actor",
      render: (v, row) =>
        v === "user"
          ? row.changedByName || row.changedByEmail || "user"
          : v === "superadmin"
          ? row.changedBySuperadminName ||
            row.changedBySuperadminEmail ||
            "superadmin"
          : "system",
    },
    { key: "ipAddress", label: "IP address" },
    {
      key: "view",
      label: "Actions",
      render: (_, row) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onRowClick(row);
          }}
          className="h-8 w-8"
        >
          <Eye size={16} />
        </Button>
      ),
    },
  ],
  [onRowClick]
);

  if (!tenantId) {
    return <div className="p-6 text-sm text-text-primary/60">Pick a tenant above to view its user actions.</div>;
  }

  return (
    <div className="flex flex-col gap-2.5">
      <Card className="flex flex-wrap items-center gap-2 p-2.5">
        <SearchInput value={search} onChange={setSearch} placeholder="Search actions, entities…" className="max-w-xs" />
        <MultiSelectFilter label="Action type" options={filterOptions.actions} value={action} onChange={setAction} />
        <MultiSelectFilter label="Entity type" options={filterOptions.entityTypes} value={entityType} onChange={setEntityType} />
        <div className="w-40">
          <Select value={actorType} onChange={setActorType} options={ACTOR_TYPES} placeholder="All actors" />
        </div>
        <DateRangeFilter name="user-actions" from={range.from} to={range.to} onChange={setRange} />
      </Card>

      <Card className="p-0 bg-table-heading border-border-primary">
        <DataTableNew
          columns={COLUMNS}
          api={api}
          search={search}
          extraParams={extraParams}
          defaultSort={{ key: "changed_at", order: "desc" }}
          dataPath="items"
          paginationLabel="actions"
        />
      </Card>

      <DetailModal
  open={detailOpen}
  onClose={() => setDetailOpen(false)}
  loading={detailLoading}
  title="Audit Log Detail"
  fields={[
    { label: "Action type", value: detail?.action },
    {
      label: "Entity",
      value: detail
        ? `${detail.entityType ?? "—"} / ${detail.entityId ?? "—"}`
        : "—",
    },
    {
      label: "Actor",
      value:
        detail?.actorType === "user"
          ? `${detail.changedByName ?? "—"} (${detail.changedByEmail ?? "—"})`
          : detail?.actorType === "superadmin"
          ? `${detail.changedBySuperadminName ?? "—"} (${detail.changedBySuperadminEmail ?? "—"})`
          : "system",
    },
    {
      label: "Changed At",
      value: detail?.changedAt
        ? new Date(detail.changedAt).toLocaleString()
        : "—",
    },
    { label: "IP Address", value: detail?.ipAddress },
    { label: "User Agent", value: detail?.userAgent },
  ]}
  jsonSections={[
    { label: "Old Value", value: detail?.oldValue },
    { label: "New Value", value: detail?.newValue },
  ]}
/>
    </div>
  );
};

export default UserActionsTab;